/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { UseEnhanceDispatch } from "components/EnhancedSpeech/useEnhanceSpeech/Reducer";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { AppAction } from "types/AppAction";
import { addToast } from "actions/ToastMessageActions";
import { acquireAccessToken, useCurrentUser } from "actions/CurrentUserActions";
import {
  DescribedEnhanceSpeechLimits,
  useEnhanceSpeechLimits,
} from "components/EnhancedSpeech/useEnhanceSpeechLimits";
import { useBuildEnhancement } from "components/EnhancedSpeech/useEnhanceSpeech/useBuildEnhancement";
import { useCallback } from "react";
import {
  enhanceFile,
  EnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  uploadFile,
} from "components/EnhancedSpeech/Utilities";
import { CurrentUser } from "reducers/CurrentUserReducer";
import { getFileDuration } from "utilities/ProjectUtilities";
import { useErrorHandler } from "components/EnhancedSpeech/useEnhanceSpeech/useErrorHandler";
import { trackEvent, PersonalizationEvent } from "api/CustomerPersonalization";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export const useProcessEnhanceSpeechFile = (
  localDispatch: UseEnhanceDispatch
): ((file: File, modelVersion: ModelVersion) => Promise<void>) => {
  const globalDispatch = useDispatch();
  const { currentUser } = useCurrentUser();
  const limits = useEnhanceSpeechLimits();
  const buildEnhancement = useBuildEnhancement(localDispatch);
  const handleError = useErrorHandler(localDispatch);

  return useCallback(
    async (file: File, modelVersion: ModelVersion) => {
      const newTrackId = uuidv4();

      let track: EnhanceSpeechTrack = {
        id: newTrackId,
        trackName: file.name,
        originalAudioUrl: null,
        enhanceSpeechAudioUrl: null,
        mergedMediaUrl: null,
        errorType: null,
        startedProcessingAt: null,
        finishedProcessingAt: null,
        duration: null,
        limits: null,
        isVideo: null,
        modelVersion: modelVersion,
      };

      localDispatch({
        type: "ADD_ENHANCEMENT",
        enhancement: buildEnhancement(track),
      });

      let errorType = await globalDispatch(
        canProcessFile(currentUser, file, limits)
      );

      if (errorType) {
        if (errorType === EnhanceSpeechTrackErrorType.AUTH_EXCEPTION) {
          return; // we don't need to send another toast
        }

        handleError({
          ...track,
          errorType,
        });

        return;
      }

      let directUploadSignedId;
      try {
        directUploadSignedId = await uploadFile(file, (percentComplete) => {
          localDispatch({
            type: "UPDATE_ENHANCEMENT_UPLOAD_PROGRESS",
            trackId: track.id,
            percentComplete,
          });
        });
      } catch (error) {
        newrelic.noticeError(error);
        errorType = EnhanceSpeechTrackErrorType.UPLOAD_FAILED;
      }

      if (errorType || !directUploadSignedId) {
        handleError({
          ...track,
          errorType,
        });

        return;
      }

      track = await enhanceFile(file, directUploadSignedId, track);

      if (track.errorType) {
        handleError(track);
      } else {
        localDispatch({
          type: "UPDATE_ENHANCEMENT",
          enhancement: buildEnhancement(track),
        });
        trackEvent(PersonalizationEvent.USER_ENHANCES_SPEECH);
      }
    },
    [
      buildEnhancement,
      currentUser,
      globalDispatch,
      handleError,
      limits,
      localDispatch,
    ]
  );
};

const canProcessFile = (
  currentUser: CurrentUser | null,
  file: File,
  limits: DescribedEnhanceSpeechLimits
): AppAction<Promise<EnhanceSpeechTrackErrorType | null>> => {
  return async (dispatch): Promise<EnhanceSpeechTrackErrorType | null> => {
    if (!currentUser?.hasEnhanceSpeechAccess) {
      dispatch(
        addToast({
          message: I18n.t("enhance_speech.access_disabled"),
        })
      );
      return EnhanceSpeechTrackErrorType.AUTH_EXCEPTION;
    }

    const accessToken = await acquireAccessToken();
    if (!accessToken) {
      return EnhanceSpeechTrackErrorType.AUTH_EXCEPTION;
    }

    if (file.size > limits.fileSizeLimitBytes) {
      newrelic.noticeError("Rejected file for enhancement due to size", {
        size: file.size,
        fileName: file.name,
        expected: "true",
      });

      return EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT;
    }

    let fileDuration: number;
    try {
      fileDuration = await getFileDuration(file);
    } catch (error) {
      //Log error but return null and try to process on backend
      newrelic.noticeError(error, {
        fileName: file.name,
        fileType: file.type,
        expected: "true",
      });
      return null;
    }
    if (fileDuration > limits.fileDurationLimitSeconds) {
      newrelic.noticeError("Rejected file for enhancement due to duration", {
        duration: fileDuration,
        fileName: file.name,
        expected: "true",
      });
      return EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT;
    }

    return null;
  };
};
